
// eslint-disable-next-line no-unused-vars
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { 
  FaImage, 
  FaUsers, 
  FaCalculator, 
  FaMapMarkerAlt, 
  FaTrash, 
  FaPlus,
  FaSave,
  FaSpinner
} from 'react-icons/fa';
import './HomePageAdmin.css';

const HomePageAdmin = () => {
  const [activeTab, setActiveTab] = useState('hero');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  // eslint-disable-next-line no-unused-vars
  // State for all sections
  const [heroSlides, setHeroSlides] = useState([]);
  const [introText, setIntroText] = useState('');
  const [numbers, setNumbers] = useState({
    years: '',
    projects: '',
    awards: '',
    clients: ''
  });
  const [categories, setCategories] = useState({
    urbanism: { description: '', images: [] },
    architecture: { description: '', images: [] },
    interior: { description: '', images: [] }
  });
  const [clientLogos, setClientLogos] = useState([]);
  const [contacts, setContacts] = useState({
    address: '',
    email: '',
    phone1: '',
    phone2: '',
    mobile1: '',
    map: { lat: '', long: '' }
  });
// eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);

      // Fetch details (carousel, intro, contacts)
      const detailsRef = doc(db, 'home', 'details');
      const detailsSnap = await getDoc(detailsRef);
      if (detailsSnap.exists()) {
        const data = detailsSnap.data();
        
        if (data.carousel) {
          setHeroSlides(Object.values(data.carousel).sort((a, b) => a.order - b.order));
        }
        
        if (data.contacts) {
          setContacts(data.contacts);
        }
      }

      // Fetch numbers
      const numbersRef = doc(db, 'home', 'numbers');
      const numbersSnap = await getDoc(numbersRef);
      if (numbersSnap.exists()) {
        setNumbers(numbersSnap.data());
      }

      // Fetch clients
      const clientsRef = doc(db, 'home', 'clients');
      const clientsSnap = await getDoc(clientsRef);
      if (clientsSnap.exists()) {
        setClientLogos(Object.values(clientsSnap.data()));
      }

      // Fetch categories
      const categoriesRef = doc(db, 'home', 'categories');
      const categoriesSnap = await getDoc(categoriesRef);
      if (categoriesSnap.exists()) {
        const data = categoriesSnap.data();
        setCategories({
          urbanism: { 
            description: 'Regarding contemporary urban planning, we strive to create city-scale developments that enhance the community.',
            images: data.urbanism || [] 
          },
          architecture: { 
            description: 'In modern architecture, we aim to create buildings that enhance health and happiness with luxury.',
            images: data.architecture || [] 
          },
          interior: { 
            description: 'In interior design, we strive to create luxurious, entertaining, and comfortable spaces.',
            images: data.interior || [] 
          }
        });
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      showMessage('Error loading data', 'error');
      setLoading(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  // Hero Slides Functions
  const handleHeroImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setSaving(true);
      const storageRef = ref(storage, `home/hero/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      const newSlide = {
        url,
        label: '',
        order: heroSlides.length
      };

      const updatedSlides = [...heroSlides, newSlide];
      setHeroSlides(updatedSlides);
      
      await saveHeroSlides(updatedSlides);
      showMessage('Hero slide added successfully', 'success');
    } catch (error) {
      console.error('Error uploading hero image:', error);
      showMessage('Error uploading image', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateHeroSlideLabel = (index, label) => {
    const updated = [...heroSlides];
    updated[index].label = label;
    setHeroSlides(updated);
  };

  const deleteHeroSlide = async (index) => {
    if (!window.confirm('Delete this hero slide?')) return;

    try {
      setSaving(true);
      const updated = heroSlides.filter((_, i) => i !== index);
      setHeroSlides(updated);
      await saveHeroSlides(updated);
      showMessage('Hero slide deleted', 'success');
    } catch (error) {
      showMessage('Error deleting slide', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveHeroSlides = async (slides) => {
    const carousel = {};
    slides.forEach((slide, index) => {
      carousel[`slide${index}`] = { ...slide, order: index };
    });

    await updateDoc(doc(db, 'home', 'details'), { carousel });
  };

  // Numbers Functions
  const handleNumbersChange = (field, value) => {
    setNumbers(prev => ({ ...prev, [field]: value }));
  };

  const saveNumbers = async () => {
    try {
      setSaving(true);
      await setDoc(doc(db, 'home', 'numbers'), numbers);
      showMessage('Numbers updated successfully', 'success');
    } catch (error) {
      showMessage('Error saving numbers', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Category Functions
  const handleCategoryImageUpload = async (e, category) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setSaving(true);
      const storageRef = ref(storage, `home/categories/${category}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      const updated = { ...categories };
      updated[category].images.push(url);
      setCategories(updated);

      await saveCategoryImages(category, updated[category].images);
      showMessage('Category image added', 'success');
    } catch (error) {
      showMessage('Error uploading image', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteCategoryImage = async (category, index) => {
    if (!window.confirm('Delete this image?')) return;

    try {
      setSaving(true);
      const updated = { ...categories };
      updated[category].images.splice(index, 1);
      setCategories(updated);

      await saveCategoryImages(category, updated[category].images);
      showMessage('Image deleted', 'success');
    } catch (error) {
      showMessage('Error deleting image', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveCategoryImages = async (category, images) => {
    const categoriesRef = doc(db, 'home', 'categories');
    await updateDoc(categoriesRef, { [category]: images });
  };

  // Client Logos Functions
  const handleClientLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setSaving(true);
      const storageRef = ref(storage, `home/clients/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      const updated = [...clientLogos, url];
      setClientLogos(updated);

      await saveClientLogos(updated);
      showMessage('Client logo added', 'success');
    } catch (error) {
      showMessage('Error uploading logo', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteClientLogo = async (index) => {
    if (!window.confirm('Delete this client logo?')) return;

    try {
      setSaving(true);
      const updated = clientLogos.filter((_, i) => i !== index);
      setClientLogos(updated);

      await saveClientLogos(updated);
      showMessage('Client logo deleted', 'success');
    } catch (error) {
      showMessage('Error deleting logo', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveClientLogos = async (logos) => {
    const clientsObj = {};
    logos.forEach((logo, index) => {
      clientsObj[`client${index}`] = logo;
    });

    await setDoc(doc(db, 'home', 'clients'), clientsObj);
  };

  // Contacts Functions
  const handleContactChange = (field, value) => {
    if (field.includes('map.')) {
      const mapField = field.split('.')[1];
      setContacts(prev => ({
        ...prev,
        map: { ...prev.map, [mapField]: value }
      }));
    } else {
      setContacts(prev => ({ ...prev, [field]: value }));
    }
  };

  const saveContacts = async () => {
    try {
      setSaving(true);
      await updateDoc(doc(db, 'home', 'details'), { contacts });
      showMessage('Contact info updated', 'success');
    } catch (error) {
      showMessage('Error saving contacts', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <FaSpinner className="spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="homepage-admin">
      <div className="admin-header">
        <h1>Homepage Management</h1>
        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}
      </div>

      <div className="admin-tabs">
        <button 
          className={activeTab === 'hero' ? 'active' : ''} 
          onClick={() => setActiveTab('hero')}
        >
          <FaImage /> Hero Slider
        </button>
        <button 
          className={activeTab === 'numbers' ? 'active' : ''} 
          onClick={() => setActiveTab('numbers')}
        >
          <FaCalculator /> Numbers
        </button>
        <button 
          className={activeTab === 'categories' ? 'active' : ''} 
          onClick={() => setActiveTab('categories')}
        >
          <FaImage /> Categories
        </button>
        <button 
          className={activeTab === 'clients' ? 'active' : ''} 
          onClick={() => setActiveTab('clients')}
        >
          <FaUsers /> Clients
        </button>
        <button 
          className={activeTab === 'contacts' ? 'active' : ''} 
          onClick={() => setActiveTab('contacts')}
        >
          <FaMapMarkerAlt /> Contacts
        </button>
      </div>

      <div className="admin-content">
        {/* Hero Slider Tab */}
        {activeTab === 'hero' && (
          <div className="section">
            <h2>Hero Slider Images</h2>
            <div className="upload-area">
              <label className="upload-btn">
                <FaPlus /> Add Hero Image
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleHeroImageUpload}
                  disabled={saving}
                />
              </label>
            </div>

            <div className="hero-slides-grid">
              {heroSlides.map((slide, index) => (
                <div key={index} className="hero-slide-card">
                  <img src={slide.url} alt={`Slide ${index + 1}`} />
                  <input
                    type="text"
                    placeholder="Slide label"
                    value={slide.label}
                    onChange={(e) => updateHeroSlideLabel(index, e.target.value)}
                  />
                  <div className="card-actions">
                    <button 
                      className="btn-save"
                      onClick={() => saveHeroSlides(heroSlides)}
                      disabled={saving}
                    >
                      <FaSave /> Save Label
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => deleteHeroSlide(index)}
                      disabled={saving}
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Numbers Tab */}
        {activeTab === 'numbers' && (
          <div className="section">
            <h2>AAC in Numbers</h2>
            <div className="numbers-grid">
              <div className="form-group">
                <label>Years</label>
                <input
                  type="number"
                  value={numbers.years}
                  onChange={(e) => handleNumbersChange('years', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Projects</label>
                <input
                  type="number"
                  value={numbers.projects}
                  onChange={(e) => handleNumbersChange('projects', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Awards</label>
                <input
                  type="number"
                  value={numbers.awards}
                  onChange={(e) => handleNumbersChange('awards', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Clients</label>
                <input
                  type="number"
                  value={numbers.clients}
                  onChange={(e) => handleNumbersChange('clients', e.target.value)}
                />
              </div>
            </div>
            <button 
              className="btn-save-main"
              onClick={saveNumbers}
              disabled={saving}
            >
              <FaSave /> {saving ? 'Saving...' : 'Save Numbers'}
            </button>
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="section">
            <h2>Category Images</h2>
            
            {['urbanism', 'architecture', 'interior'].map(category => (
              <div key={category} className="category-section">
                <h3>{category.charAt(0).toUpperCase() + category.slice(1)}</h3>
                
                <div className="upload-area">
                  <label className="upload-btn">
                    <FaPlus /> Add Image
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => handleCategoryImageUpload(e, category)}
                      disabled={saving}
                    />
                  </label>
                </div>

                <div className="images-grid">
                  {categories[category].images.map((img, index) => (
                    <div key={index} className="image-card">
                      <img src={img} alt={`${category} ${index + 1}`} />
                      <button 
                        className="btn-delete"
                        onClick={() => deleteCategoryImage(category, index)}
                        disabled={saving}
                      >
                        <FaTrash /> Delete
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Clients Tab */}
        {activeTab === 'clients' && (
          <div className="section">
            <h2>Client Logos</h2>
            
            <div className="upload-area">
              <label className="upload-btn">
                <FaPlus /> Add Client Logo
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleClientLogoUpload}
                  disabled={saving}
                />
              </label>
            </div>

            <div className="images-grid">
              {clientLogos.map((logo, index) => (
                <div key={index} className="image-card">
                  <img src={logo} alt={`Client ${index + 1}`} />
                  <button 
                    className="btn-delete"
                    onClick={() => deleteClientLogo(index)}
                    disabled={saving}
                  >
                    <FaTrash /> Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="section">
            <h2>Contact Information</h2>
            
            <div className="contacts-form">
              <div className="form-group">
                <label>Address</label>
                <textarea
                  value={contacts.address}
                  onChange={(e) => handleContactChange('address', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={contacts.email}
                  onChange={(e) => handleContactChange('email', e.target.value)}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone 1</label>
                  <input
                    type="tel"
                    value={contacts.phone1}
                    onChange={(e) => handleContactChange('phone1', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Phone 2</label>
                  <input
                    type="tel"
                    value={contacts.phone2}
                    onChange={(e) => handleContactChange('phone2', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Mobile</label>
                <input
                  type="tel"
                  value={contacts.mobile1}
                  onChange={(e) => handleContactChange('mobile1', e.target.value)}
                />
              </div>

              <h3>Map Coordinates</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Latitude</label>
                  <input
                    type="text"
                    value={contacts.map.lat}
                    onChange={(e) => handleContactChange('map.lat', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Longitude</label>
                  <input
                    type="text"
                    value={contacts.map.long}
                    onChange={(e) => handleContactChange('map.long', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <button 
              className="btn-save-main"
              onClick={saveContacts}
              disabled={saving}
            >
              <FaSave /> {saving ? 'Saving...' : 'Save Contact Info'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePageAdmin;